import { useTranslation } from "react-i18next";
import { cn } from "@core/utils";
import type { DossierStep } from "@modules/applications/_shared";

/**
 * Per-step accent for the chip's dot + the wash behind it (Figma `430:17264` "Projet",
 * `431:17904` "Équipe", `432:18511` "Détails pratiques" — founder reuses `brand`, already the
 * platform's primary accent, so it has no entry of its own to fetch).
 *
 * `menthe-700`/`menthe-200`/`gold-600`/`gold-200` were added to `theme.css` for this component —
 * the ramp only had 50/600/800 (menthe) and 50/800 (gold) before, neither an exact match for the
 * shades these two steps' chips actually use.
 */
const STEP_ACCENT: Record<
  DossierStep,
  { dot: string; dotBorder: string; wash: string }
> = {
  founder: {
    dot: "bg-brand-600",
    dotBorder: "border-brand-300",
    wash: "from-brand-50",
  },
  project: {
    dot: "bg-menthe-700",
    dotBorder: "border-menthe-200",
    wash: "from-menthe-50",
  },
  team: {
    dot: "bg-menthe-700",
    dotBorder: "border-menthe-200",
    wash: "from-menthe-50",
  },
  practical: {
    dot: "bg-gold-600",
    dotBorder: "border-gold-200",
    wash: "from-gold-50",
  },
};

/**
 * « Intro — étape » (Figma `426:15264` and siblings) — the foundation shell's own step-intro
 * panel. NOT the same component as `StepIntro` in `DossierChrome.tsx`: that one belongs to the
 * hidden field-by-field build (still used by `ApplicationScreen.tsx`) and is left untouched on
 * purpose. Its chip predates the 2026-08-17 revision Figma's own component description calls
 * out (a pill with a round dot); the current spec is a radius-8 chip with a rounded-SQUARE dot
 * (12×12, r4) — reproducing that here as a fresh component is simpler and safer than reworking a
 * component another, frozen screen still depends on.
 *
 * The gradient wash is a `from-{accent}-50 to-surface-card` fade rather than a literal copy of
 * Figma's exported gradient stops — those come out as percentages past 100% (a rotated-fill export
 * artifact), which do not reproduce the same visual in CSS. The `*-50` tokens already exist as
 * exactly this kind of light tint-to-white surface (see their own doc comment in `theme.css`).
 *
 * ⚠️ Only ever mounted as the FIRST child of `DossierFoundationScreen`'s `<main>` — `-mt-xl`
 * cancels that container's own top padding so the wash sits flush under the stepper, the way
 * Figma draws it, rather than stacking a second gap on top of `<main>`'s own.
 */
export function FoundationStepIntro({
  step,
  index,
  total,
  title,
  description,
}: {
  step: DossierStep;
  index: number;
  total: number;
  title: string;
  description: string;
}) {
  const { t } = useTranslation("acquire");
  const accent = STEP_ACCENT[step];

  return (
    <div
      className={cn(
        "-mx-md -mt-xl flex flex-col gap-lg bg-linear-to-b to-surface-card px-md pt-xl pb-xl sm:-mx-xl sm:px-xl",
        accent.wash
      )}
    >
      <span className="inline-flex h-6.5 w-fit items-center gap-xs rounded-lg border border-gray-300 bg-surface-card px-sm">
        <span
          aria-hidden
          className={cn(
            "size-3 shrink-0 rounded border",
            accent.dot,
            accent.dotBorder
          )}
        />
        <span className="font-heading text-xs font-semibold text-gray-700">
          {t("dossier.step.chip", { index, total })}
        </span>
      </span>

      <div className="flex flex-col gap-xxs">
        <h1 className="display-xs-semibold text-(--text-primary)">{title}</h1>
        <p className="text-md-regular text-(--text-secondary)">{description}</p>
      </div>
    </div>
  );
}
